import { useState } from "react";
import { useDispatch } from "react-redux";
import { setSelectedProduct } from "../../../api/reducers/selectedProductReducer";
import RichTextDisplay from "../../../components/RichTextDisplay";

const COURSE_GRADIENTS = [
  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
  "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
];

const getProductImage = (product, index) => {
  if (product?.imageContents?.length > 0) {
    return product.imageContents[0].displayLink || product.imageContents[0].storedLink;
  }
  if (product?.imageUrlsList?.length > 0) {
    return product.imageUrlsList[0];
  }
  if (product?.imageURL) {
    return product.imageURL;
  }
  return COURSE_GRADIENTS[index % COURSE_GRADIENTS.length];
};

const isGradientImage = (imageSrc = "") => imageSrc.includes("gradient");

const FeaturedCourses = ({ productList = [], navigate, loggedIn }) => {
  const dispatch = useDispatch();
  const [hoveredCard, setHoveredCard] = useState(null);

  const handleProductClick = (product) => {
    if (loggedIn) {
      dispatch(setSelectedProduct(product));
      navigate("/course-detail");
      return;
    }
    navigate("/sign-in");
  };

  return (
    <section
      style={{
        backgroundColor: "#ffffff",
        padding: "4rem 0",
        textAlign: "center",
      }}
    >
      <div className="mx-auto w-full max-w-[1480px] px-4">
        <div className="text-left mb-12">
          <h4 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Featured <span className="education-primary-text">Courses</span>
          </h4>
          <p className="text-base md:text-lg text-gray-500 max-w-3xl leading-relaxed font-normal">
            Handpicked courses designed by industry experts to accelerate your career growth and skill development.
          </p>
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))] gap-6">
          {productList.slice(0, 6).map((product, index) => {
            const isHovered = hoveredCard === product.primaryId;
            const productImage = getProductImage(product, index);
            const hasRealImage = !isGradientImage(productImage);

            return (
              <div
                key={product.primaryId}
                className="group relative bg-white rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 education-card-hover-secondary hover:transform hover:-translate-y-2"
                onMouseEnter={() => setHoveredCard(product.primaryId)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => handleProductClick(product)}
              >
                <div className="relative h-44 bg-gray-100 rounded-t-lg overflow-hidden">
                  {hasRealImage ? (
                    <div className="w-full h-full flex items-center justify-center p-4">
                      <img
                        loading="lazy"
                        src={productImage}
                        alt={product.name || "Course"}
                        className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105 rounded-lg bg-white p-2 shadow-sm"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                          event.currentTarget.parentElement.style.background = COURSE_GRADIENTS[index % COURSE_GRADIENTS.length];
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center text-white text-center transition-transform duration-300 group-hover:scale-105"
                      style={{ background: productImage }}
                    >
                      <div className="text-3xl mb-2">Course</div>
                      <div className="text-sm font-semibold text-shadow">
                        {product.category?.name || "Professional Course"}
                      </div>
                    </div>
                  )}

                  {isHovered && (
                    <div className="absolute inset-0 education-gradient-overlay-secondary flex items-center justify-center">
                      <div className="text-white text-center transform transition-transform duration-300">
                        <div className="text-lg mb-1">Preview</div>
                        <div className="text-sm opacity-90">Click to explore course</div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <div className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg mb-3">
                    {product.category?.name || "Professional Development"}
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2 leading-tight">
                    {product.name}
                  </h3>

                  <RichTextDisplay
                    content={product.description || "Master essential skills with this comprehensive course designed for modern professionals."}
                    className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2"
                  />

                  <div className="flex justify-end items-center pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      className="px-4 py-2 education-primary-button text-white text-sm font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
                    >
                      Enroll
                      <span aria-hidden="true">-&gt;</span>
                    </button>
                  </div>
                </div>

                {product.isPopular && (
                  <div className="absolute top-3 right-3 px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-semibold rounded-lg">
                    Popular
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedCourses;
